import { useEffect, useRef, useState } from "react";
import { placesApi } from "../api/client";
import { loadGoogleMaps, MAP_DECLUTTER_STYLES, type MapsApi } from "../lib/googleMaps";
import { useViewport } from "../lib/shell";

export interface MapPoint {
  n: number;
  lat: number;
  lng: number;
  title: string;
}

interface Props {
  points: MapPoint[];
  /** Highlighted marker (the hovered/active place card). */
  activeN?: number | null;
  onSelect?: (n: number) => void;
}

type Phase = "loading" | "interactive" | "static" | "hidden";

interface MapCtors {
  Map: any;
  Marker: any;
  LatLngBounds: any;
}

/** Resolve the constructors the map needs, whichever bootstrap shape is live.
 *
 * With `loading=async` the script's `onload` fires once the *bootstrap* is
 * parsed, before any library is populated — reading `google.maps.Map` at that
 * instant yields undefined, and `importLibrary`'s resolved module can lag it
 * too. Rather than depend on that timing, await the imports and then poll
 * briefly for the constructors to appear on either the module or the
 * namespace. Returns null if they never do, which drops us to the static map.
 */
async function resolveMapConstructors(maps: MapsApi): Promise<MapCtors | null> {
  let mapsLib: any = null;
  let markerLib: any = null;
  let coreLib: any = null;
  if (typeof maps.importLibrary === "function") {
    [mapsLib, markerLib, coreLib] = await Promise.all([
      maps.importLibrary("maps"),
      maps.importLibrary("marker"),
      maps.importLibrary("core"),
    ]);
  }
  const ns = () => (typeof window !== "undefined" && (window as any).google?.maps) || maps;
  for (let attempt = 0; attempt < 25; attempt++) {
    const Map = mapsLib?.Map ?? ns().Map;
    const Marker = markerLib?.Marker ?? ns().Marker;
    const LatLngBounds = coreLib?.LatLngBounds ?? ns().LatLngBounds;
    if (typeof Map === "function" && typeof Marker === "function" && typeof LatLngBounds === "function") {
      return { Map, Marker, LatLngBounds };
    }
    await new Promise((r) => setTimeout(r, 100));
  }
  return null;
}

/** Interactive Google map with one numbered marker per place.
 *
 * Degrades in two steps rather than one: if the Maps JavaScript API can't be
 * loaded (key missing, API not enabled, offline) it falls back to the static
 * map image proxied by the backend, and if THAT also fails it renders nothing
 * at all — no broken image, no empty box. The panel's place cards are the
 * real payload and always render regardless.
 */
export default function PlacesMap({ points, activeN, onSelect }: Props) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<Map<number, any>>(new Map());
  const [phase, setPhase] = useState<Phase>("loading");
  // On a phone the map is ~360×200: Google's zoom + fullscreen controls
  // cover a quarter of it, and pinch/drag already do both jobs (this is
  // what the Google Maps mobile embed itself does). Bounds padding scales
  // with the canvas too — 48px of padding on a 200px-tall map collapses
  // every marker into the middle.
  const { isPhone } = useViewport();

  const theme =
    typeof document !== "undefined" &&
    document.documentElement.getAttribute("data-theme") === "daylight"
      ? "light"
      : "dark";

  // Serialized point identity — remounting the map on every parent render
  // would flatten the user's zoom/pan, which is the whole point of this
  // component, so the effect keys off the actual coordinates.
  const pointsKey = points.map((p) => `${p.n}:${p.lat},${p.lng}`).join("|");

  useEffect(() => {
    let cancelled = false;

    async function init() {
      if (!points.length) {
        setPhase("hidden");
        return;
      }
      let key = "";
      try {
        const status = await placesApi.status();
        key = status.browser_key || "";
      } catch {
        key = "";
      }
      if (cancelled) return;
      if (!key) {
        setPhase("static");
        return;
      }

      let maps: MapsApi;
      try {
        maps = await loadGoogleMaps(key);
      } catch {
        if (!cancelled) setPhase("static");
        return;
      }
      if (cancelled || !hostRef.current) return;

      // With `loading=async` the bootstrap defines only `importLibrary`;
      // `google.maps.Map` does not exist until a library is imported. The
      // direct-property path is the pre-bootstrap shape (and what the unit
      // tests' fake provides).
      let ctors: MapCtors | null;
      try {
        ctors = await resolveMapConstructors(maps);
      } catch {
        ctors = null;
      }
      if (cancelled || !hostRef.current) return;
      if (!ctors) {
        setPhase("static");
        return;
      }
      const { Map: MapCtor, Marker: MarkerCtor, LatLngBounds: BoundsCtor } = ctors;

      const map = new MapCtor(hostRef.current, {
        // Interactivity is the point: zoom, pan, scroll-wheel, keyboard.
        gestureHandling: "greedy",
        zoomControl: !isPhone,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: !isPhone,
        cameraControl: false,
        clickableIcons: false,
        // Native dark/light: recolours tiles AND Google's own controls.
        // Older API builds ignore an unknown option rather than throwing.
        colorScheme: theme === "light" ? "LIGHT" : "DARK",
        styles: MAP_DECLUTTER_STYLES,
      });
      mapRef.current = map;

      const bounds = new BoundsCtor();
      markersRef.current.clear();
      for (const p of points) {
        const marker = new MarkerCtor({
          map,
          position: { lat: p.lat, lng: p.lng },
          title: p.title,
          label: { text: String(p.n), color: "#ffffff", fontSize: "12px", fontWeight: "600" },
        });
        marker.addListener("click", () => onSelect?.(p.n));
        markersRef.current.set(p.n, marker);
        bounds.extend(marker.getPosition());
      }

      if (points.length === 1) {
        map.setCenter({ lat: points[0].lat, lng: points[0].lng });
        map.setZoom(15);
      } else {
        map.fitBounds(bounds, isPhone ? 20 : 48);
      }
      setPhase("interactive");
    }

    init();
    return () => {
      cancelled = true;
    };
    // `onSelect` is deliberately not a dependency — an inline parent callback
    // would rebuild the map (and reset zoom/pan) on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointsKey, theme, isPhone]);

  // Bounce the active marker without rebuilding the map.
  useEffect(() => {
    if (phase !== "interactive") return;
    for (const [n, marker] of markersRef.current) {
      marker.setZIndex?.(n === activeN ? 999 : undefined);
      marker.setAnimation?.(null);
    }
  }, [activeN, phase]);

  if (phase === "hidden") return null;

  if (phase === "static") {
    const pts = points.map((p) => `${p.lat},${p.lng}`).join(";");
    return (
      <div className="places-panel-map places-panel-map-static">
        <img
          src={placesApi.staticMapUrl(pts, { theme })}
          alt=""
          onError={() => setPhase("hidden")}
        />
      </div>
    );
  }

  return (
    <div className="places-panel-map">
      <div ref={hostRef} className="places-panel-map-canvas" data-testid="places-map-canvas" />
    </div>
  );
}
